import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Container,
  Grid,
  Button,
  Stack,
} from "@mui/material";

import SiteLogo from "../../Components/Assets/images/DovetailLogo.svg";

import OuterHeader1 from "../../Components/header/Header/OuterHeader1";
import LoginIcon from "@mui/icons-material/Login";
import { useSelector } from "react-redux";
import bk from "../../Components/Assets/images/wbk.png";
import configServ from "../../services/config";
import Skeleton from "@mui/material/Skeleton";
import SupportButton from "./SupportButton";

// import Inquiry from "../../Components/Inquiry/Inquiry";

const Welcome = () => {
  const lightMode = useSelector(
    (state) => state.GlobalVariables.lightMode
  );

  const inquiryRef = useRef(null);
  const navigate = useNavigate();

  const [companyLogo, setCompanyLogo] = useState(null);
  const [companyName, SetCompanyName] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // ==========================
  // Fetch Primary Company
  // ==========================

  const fetchPrimaryCompany = async () => {
    try {
      const res = await configServ.getPrimaryCompany();

      if (res.status === 200) {
        setCompanyLogo(res.data.CompanyLogo);
        SetCompanyName(res.data.CompanyName);
      }
    } catch (e) {
      console.log(e);
    } finally {
      setIsLoading(false);
    }
  };

  // ==========================
  // Load Company Data
  // ==========================

  useEffect(() => {
    fetchPrimaryCompany();
  }, []);

  // ==========================
  // Default Content
  // ==========================

  const lines = [
    "Dovetail-crm is a technology-driven manufacturing company delivering Billing, Payment Delivery Solutions for Small Medium Businesses.",
    "This software is the web version of Accounting Software.",
    "It provide flexibility to the user to use web application for invoicing and web application but data will be synced and will provide flexibility to the user.",
  ];

  return (
    <>
      <Box
        sx={{
          width: "100vw",
          minHeight: "100vh",
          height: "100vh",

          position: "relative",

          backgroundColor: lightMode
            ? "#ffffff"
            : "#090e34",

          overflow: "hidden",

          // ==========================================
          // BACKGROUND IMAGE
          // ==========================================

          "&::before": {
            content: '""',

            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,

            backgroundImage: `url(${bk})`,

            backgroundSize: "cover",

            backgroundPosition: "left center",

            backgroundRepeat: "no-repeat",

            // ======================================
            // LIGHT MODE FIX
            // ======================================

            filter: lightMode
              ? "saturate(2.5) contrast(1.25) brightness(0.98)"
              : "none",

            // White background par transparent
            // blue image ko visible rakhega
            mixBlendMode: lightMode
              ? "multiply"
              : "normal",

            opacity: lightMode ? 1 : 1,

            zIndex: 0,

            pointerEvents: "none",
          },

          // ==========================================
          // CONTENT ABOVE BACKGROUND
          // ==========================================

          "& > *": {
            position: "relative",
            zIndex: 1,
          },
        }}
      >
        {/* ==========================
            Header
        ========================== */}

        <OuterHeader1 companyLogo={companyLogo} />

        {/* ==========================
            Main Content
        ========================== */}

        <Container
          component="main"
          maxWidth="md"
          sx={{
            mt: {
              xs: 25,
              sm: 25,
              md: 20,
            },
          }}
        >
          {/* ==========================
              Company Logo
          ========================== */}

          <Stack
            justifyContent="center"
            alignItems="center"
            spacing={3}
            sx={{
              width: "100%",
            }}
          >
            {isLoading ? (
              <Stack spacing={1}>
                <Skeleton
                  variant="circular"
                  width={100}
                  height={100}
                />
              </Stack>
            ) : (
              <Box
                component="img"
                src={
                  companyLogo
                    ? `data:image/png;base64,${companyLogo}`
                    : SiteLogo
                }
                alt="Company Logo"
                sx={{
                  width: {
                    xs: "75%",
                    sm: "65%",
                    md: "50%",
                  },

                  maxWidth: "600px",

                  height: "auto",

                  mb: {
                    xs: 3,
                    sm: 4,
                    md: 4,
                  },

                  filter: "none",

                  display: "block",
                }}
              />
            )}
          </Stack>

          {/* ==========================
              Company Name / Description
          ========================== */}

          <Stack width="100%">
            <Box
              sx={{
                width: "100%",

                textAlign: "center",

                px: {
                  xs: 1,
                  sm: 2,
                  md: 2,
                },
              }}
            >
              {isLoading ? (
                <Grid
                  container
                  justifyContent="center"
                  alignItems="center"
                  sx={{
                    minHeight: "100px",
                  }}
                >
                  <Box
                    sx={{
                      width: {
                        xs: "90%",
                        sm: 600,
                      },
                    }}
                  >
                    <Skeleton />
                    <Skeleton animation="wave" />
                    <Skeleton animation={false} />
                  </Box>
                </Grid>
              ) : companyName ? (
                <Typography
                  variant="h4"
                  component="h2"
                  sx={{
                    fontWeight: 700,

                    color: lightMode
                      ? "#222222"
                      : "#ffffff",

                    letterSpacing: {
                      xs: 1,
                      sm: 1,
                      md: 2,
                    },

                    width: "100%",

                    textAlign: "center",

                    fontSize: {
                      xs: "1.5rem",
                      sm: "1.8rem",
                      md: "2.5rem",
                      lg: "2.5rem",
                    },

                    lineHeight: 1.3,

                    mb: 1,
                  }}
                >
                  {companyName}
                </Typography>
              ) : (
                lines.map((line, index) => (
                  <Typography
                    key={index}
                    variant="body1"
                    sx={{
                      color: lightMode
                        ? "#222222"
                        : "#ffffff",

                      fontSize: {
                        xs: "14px",
                        md: "16px",
                      },

                      fontWeight: 600,

                      lineHeight: "24px",
                    }}
                  >
                    {line}
                  </Typography>
                ))
              )}
            </Box>

            {/* ==========================
                Staff Login Button
            ========================== */}

            <Box
              sx={{
                display: "flex",

                justifyContent: "center",

                alignItems: "center",

                mt: {
                  xs: 3,
                  sm: 3,
                  md: 3,
                },
              }}
            >
              <Button
                variant="contained"
                onClick={() =>
                  navigate("/admin-login", {
                    state: {
                      organisation: true,
                    },
                  })
                }
                startIcon={<LoginIcon />}
                sx={{
                  minWidth: {
                    xs: "175px",
                    sm: "175px",
                  },

                  height: "54px",

                  px: 3,

                  borderRadius: "28px",

                  backgroundColor: "#1976d2",

                  fontSize: "16px",

                  fontWeight: 500,

                  textTransform: "uppercase",

                  boxShadow:
                    "0px 5px 12px rgba(0, 0, 0, 0.20)",

                  transition: "all 0.2s ease-in-out",

                  "&:hover": {
                    backgroundColor: "#1565c0",

                    boxShadow:
                      "0px 7px 16px rgba(0, 0, 0, 0.25)",

                    transform: "translateY(-1px)",
                  },

                  "&:active": {
                    transform: "translateY(0px)",
                  },

                  "& .MuiButton-startIcon": {
                    marginRight: "8px",
                  },
                }}
              >
                Staff Login
              </Button>
            </Box>
          </Stack>

          {/* ==========================
              Support Button
          ========================== */}

          <Stack
            justifyContent="center"
            alignItems="center"
            sx={{
              mt: 1.5,

              mb: {
                xs: 2,
                sm: 3,
              },
            }}
          >
            <SupportButton />
          </Stack>
        </Container>
      </Box>
    </>
  );
};

export default Welcome;